import { Button, Card, CardContent, CardHeader, CardTitle, CircularLoader, Toaster } from "@prelude-ai/design-system"
import * as Sentry from "@sentry/react"
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { createRouter, Link, RouterProvider } from "@tanstack/react-router"
import { Ghost } from "lucide-react"
import React from "react"
import ReactDOM from "react-dom/client"
import "./index.css"
import "@prelude-ai/design-system/styles.css"
import { ThemeProvider } from "./components/ThemeProvider"
import { routeTree } from "./routeTree.gen"
import { toast } from "./utils/toast.tsx"

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_VERCEL_TARGET_ENV,
  enableLogs: true,
  integrations: [Sentry.browserTracingIntegration(), Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] })],
  tracesSampleRate: 0.3
})

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      toast.error("Query failed", {
        description: `[${query.queryKey.join("/")}] ${error.message}`
      })
    }
  })
})

// Use purple favicon in development
if (import.meta.env.DEV) {
  const link = document.querySelector("link[rel='icon']") as HTMLLinkElement
  if (link) {
    link.href = "/src/frontend/favicon-dev.svg"
  }
}

const router = createRouter({
  routeTree,
  defaultViewTransition: true,
  defaultPendingComponent: () => {
    return (
      <div className="flex h-screen items-center justify-center">
        <CircularLoader size="lg" />
      </div>
    )
  },
  defaultErrorComponent: ({ error }) => (
    <div className="flex h-screen items-center justify-center p-8">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle className="text-warning">Error {error.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <pre className="overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-4 text-sm text-muted-foreground">
            {error.message}
          </pre>
        </CardContent>
      </Card>
    </div>
  ),
  defaultNotFoundComponent: () => {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 p-8 text-center">
        <Ghost className="h-16 w-16 text-muted-foreground" />
        <h1 className="text-2xl font-semibold text-muted-foreground">404 - Not Found</h1>
        <p className="text-sm text-muted-foreground">The page you're looking for doesn't exist.</p>
        <Button variant="outline" asChild>
          <Link to="/new" search={{}}>
            Go Home
          </Link>
        </Button>
      </div>
    )
  }
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById("root")
if (!rootElement) {
  throw new Error("Root element not found")
}

ReactDOM.createRoot(rootElement, {
  onCaughtError: Sentry.reactErrorHandler()
}).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark" storageKey="prelude-agents-theme">
        <RouterProvider router={router} />
        <Toaster position="top-center" />
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
)
